// Cooperative, frame-budgeted mount scheduler.
//
// The canvas mounts thousands of Vue components when a populated DC loads.
// Mounting is fundamentally main-thread work, but we can keep the UI
// responsive by spreading it across frames with a strict time budget per
// frame — the browser then has room to handle pointer events, paint, and
// scroll between batches.
//
// The single shared queue is the key win: every breakpoint frame and every
// container element pushes its child-mount jobs into ONE queue. A single
// rAF-driven worker pulls jobs until the per-frame budget is consumed,
// then yields. Without the global queue, parallel frames each scheduled
// their own rAF and the browser wound up doing serialized work across
// multiple rAF callbacks per frame anyway — but now we cap the total
// time spent in any one frame to `FRAME_BUDGET_MS`, period.
//
// Use:
//
//   import { scheduleMount } from "%/dynamic-component-editor/utils/mount-scheduler";
//   const cancel = scheduleMount(() => {
//     this.mountedChildIds.push(nextId);
//   });
//   // optional: cancel() before the work runs to drop the job

// Per-frame budget. 8 ms gives back half of a 16.6 ms frame for paint,
// scroll, pointer handling, and other reactivity. Larger values mount
// faster but leave less room for the user; smaller values are smoother
// but mount slower. The scheduler ALSO defers entirely while the user
// is actively interacting — see `INTERACTION_QUIESCE_MS`.
const FRAME_BUDGET_MS = 8;

// Initial-mount budget. Fresh page-load and other "user is staring at
// a spinner" moments use this larger budget: there's no panning or
// scrolling to keep smooth, so giving the mount loop most of the frame
// drains hundreds of components in a handful of frames instead of
// hundreds. Stays below 16.6 ms so we still leave room for paint and
// browser bookkeeping.
//
// Burst is ON until the user first interacts — or, inside an armed
// hydration window (see beginHydrationWindow), until the window ends.
const INITIAL_BURST_FRAME_BUDGET_MS = 14;

// Canvas-armed initial-mount window: burst continues through passive pointer
// movement (only drags pause), deadline-capped so a stuck frame can't leak it.
const HYDRATION_WINDOW_MAX_MS = 15000;
let hydrationWindowEndsAt = 0;

export function beginHydrationWindow() {
  hydrationWindowEndsAt = now() + HYDRATION_WINDOW_MAX_MS;
  // A pending recheck timer or slow idle callback would swallow the kick.
  clearRecheck();
  cancelScheduledFrame();
  scheduleNextFrame();
}

export function endHydrationWindow() {
  hydrationWindowEndsAt = 0;
}

function isHydrationWindowActive() {
  return hydrationWindowEndsAt > 0 && now() < hydrationWindowEndsAt;
}

function isInitialBurstActive() {
  return lastInteractionAt === 0 || isHydrationWindowActive();
}

// Pause threshold. Any pointer / mouse / wheel / touch / keyboard event
// stamps `lastInteractionAt`; the scheduler stays paused until at least
// this many ms have passed without a fresh event AND no drag is in
// flight. Without this, jobs ran during natural mid-pan pauses (the
// brief stop most users do every few seconds while panning) and the
// next pointermove had to wait for the in-flight 20-30 ms mount job to
// finish — the user-perceived "stuck on resume" pulse.
//
// 400 ms is conservative enough to cover the typical pause-then-resume
// rhythm during panning, at the cost of pushing initial hydration of
// off-screen frames into a moment of genuine stillness. The spinner-
// gated frame keeps that user-visible — they see "loading" until they
// stop.
const INTERACTION_QUIESCE_MS = 400;

// Additional pause window after a drag (mousedown → mouseup) ends.
// Pan via mousedown often involves user moving the cursor without
// releasing for a while; we want to keep the scheduler paused for the
// entire drag plus a settle.
const DRAG_RELEASE_GRACE_MS = 250;

// Check this often (ms) when re-arming after deferred work.
const RECHECK_INTERVAL_MS = 80;

// Upper bound before an idle-callback mount runs anyway — steady-state
// hydration can't be starved indefinitely by a busy main thread.
const IDLE_CALLBACK_TIMEOUT_MS = 200;

const queue = [];
let frameId = null;
let frameMode = null;
let recheckTimer = null;
let nextJobId = 1;
let lastInteractionAt = 0;
// Active drag tracking — mousedown / pointerdown set true, the matching
// up event sets false plus stamps `lastInteractionAt` so the standard
// quiesce window applies. While `isDragging` is true the scheduler is
// hard-paused regardless of how long ago the last move event was.
let isDragging = false;
// Global drain bookkeeping: fires once per busy → idle transition lasting
// DRAIN_SETTLE_MS. Consumed by the store's paste flow; frames use onFrameDrain.
const DRAIN_SETTLE_MS = 80;
const drainSubscribers = new Set();
let hasBeenBusy = false;
let drainTimer = null;

// Per-frame drain tracking: jobs carry a frameKey; a frame's subscribers
// fire once its own count has been zero for DRAIN_SETTLE_MS.
const frameJobCounts = new Map();
const frameDrainSubscribers = new Map();
const frameDrainTimers = new Map();

function bumpFrameJobCount(frameKey, delta) {
  if (!frameKey) {
    return;
  }
  const count = Math.max(0, (frameJobCounts.get(frameKey) ?? 0) + delta);
  frameJobCounts.set(frameKey, count);
  if (count > 0) {
    const timer = frameDrainTimers.get(frameKey);
    if (timer != null) {
      clearTimeout(timer);
      frameDrainTimers.delete(frameKey);
    }
    return;
  }
  armFrameDrainTimer(frameKey);
}

function armFrameDrainTimer(frameKey) {
  if (frameDrainTimers.has(frameKey)) {
    return;
  }
  const timer = setTimeout(() => {
    frameDrainTimers.delete(frameKey);
    if ((frameJobCounts.get(frameKey) ?? 0) > 0) {
      return;
    }
    const subscribers = frameDrainSubscribers.get(frameKey);
    if (!subscribers) {
      return;
    }
    for (const callback of [...subscribers]) {
      try {
        callback();
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("[mount-scheduler] frame-drain subscriber threw", err);
      }
    }
  }, DRAIN_SETTLE_MS);
  frameDrainTimers.set(frameKey, timer);
}

// Fires once the frame's jobs have been idle one settle window — including
// immediately-after-settle when the frame is already idle at subscribe time.
export function onFrameDrain(frameKey, handler) {
  let subscribers = frameDrainSubscribers.get(frameKey);
  if (!subscribers) {
    subscribers = new Set();
    frameDrainSubscribers.set(frameKey, subscribers);
  }
  subscribers.add(handler);
  if ((frameJobCounts.get(frameKey) ?? 0) === 0) {
    armFrameDrainTimer(frameKey);
  }
  return () => subscribers.delete(handler);
}

const supportsIdleCallback = typeof window !== "undefined"
  && typeof window.requestIdleCallback === "function";

function now() {
  return typeof performance !== "undefined" ? performance.now() : Date.now();
}

function markInteraction() {
  lastInteractionAt = now();
}

if (typeof window !== "undefined") {
  // Capture-phase listeners on document so we see the events before any
  // of the canvas's own handlers can stop them. Passive so we don't
  // affect scroll performance ourselves.
  const opts = { capture: true, passive: true };
  document.addEventListener("pointermove", markInteraction, opts);
  document.addEventListener("mousemove", markInteraction, opts);
  document.addEventListener("wheel", markInteraction, opts);
  document.addEventListener("touchmove", markInteraction, opts);
  document.addEventListener("touchstart", markInteraction, opts);
  document.addEventListener("keydown", markInteraction, opts);
  // Pointer / mouse down marks "drag in progress" — the scheduler stays
  // hard-paused until the matching up event PLUS DRAG_RELEASE_GRACE_MS,
  // because a drag (canvas pan, layers-panel reorder) is the highest-
  // latency-sensitivity user gesture we can have.
  document.addEventListener("pointerdown", () => {
    isDragging = true;
    markInteraction();
  }, opts);
  document.addEventListener("mousedown", () => {
    isDragging = true;
    markInteraction();
  }, opts);
  document.addEventListener("pointerup", () => {
    isDragging = false;
    lastInteractionAt = now() + DRAG_RELEASE_GRACE_MS - INTERACTION_QUIESCE_MS;
  }, opts);
  document.addEventListener("mouseup", () => {
    isDragging = false;
    lastInteractionAt = now() + DRAG_RELEASE_GRACE_MS - INTERACTION_QUIESCE_MS;
  }, opts);
  document.addEventListener("pointercancel", () => {
    isDragging = false;
    markInteraction();
  }, opts);
  // Page-visibility changes always reset interaction so a tab switch
  // doesn't leave the scheduler paused indefinitely on a stale
  // lastInteractionAt.
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") {
      markInteraction();
    }
  }, opts);
}

function clearRecheck() {
  if (recheckTimer != null) {
    clearTimeout(recheckTimer);
    recheckTimer = null;
  }
}

function cancelScheduledFrame() {
  if (frameId == null) {
    return;
  }
  if (frameMode === "idle") {
    window.cancelIdleCallback(frameId);
  } else if (frameMode === "raf") {
    cancelAnimationFrame(frameId);
  } else {
    clearTimeout(frameId);
  }
  frameId = null;
  frameMode = null;
}

function isPaused() {
  if (isDragging) {
    return true;
  }
  // In-window: only drags pause (note: this also skips DRAG_RELEASE_GRACE_MS).
  if (isHydrationWindowActive()) {
    return false;
  }
  return now() - lastInteractionAt < INTERACTION_QUIESCE_MS;
}

function scheduleNextFrame() {
  if (frameId != null || recheckTimer != null) {
    return;
  }
  // While a drag is in flight or the user has touched anything within
  // INTERACTION_QUIESCE_MS, defer to a setTimeout that re-checks. Pure
  // idleCallback can fire during interaction when the browser thinks
  // it has spare time (between events) — that's the source of the
  // mid-pan freeze pulses, because once a 20-30 ms mount job starts
  // the JS event loop can't preempt it.
  if (isPaused()) {
    const sinceInteraction = now() - lastInteractionAt;
    const wait = isDragging
      ? RECHECK_INTERVAL_MS
      : Math.max(RECHECK_INTERVAL_MS, INTERACTION_QUIESCE_MS - sinceInteraction);
    recheckTimer = setTimeout(() => {
      recheckTimer = null;
      scheduleNextFrame();
    }, wait);
    return;
  }
  // During the initial burst we want every frame, not idle frames — the
  // page is loading, the user is staring at a spinner / empty canvas,
  // and waiting for the browser to declare itself idle wastes time.
  // After the user interacts we revert to idle-callback so editing stays
  // smooth (the cost of a mid-edit mount cascade competing with paint
  // is the original reason this throttle exists).
  if (!isInitialBurstActive() && supportsIdleCallback) {
    frameMode = "idle";
    frameId = window.requestIdleCallback(runFrame, { timeout: IDLE_CALLBACK_TIMEOUT_MS });
  } else if (typeof requestAnimationFrame === "function") {
    frameMode = "raf";
    frameId = requestAnimationFrame(runFrame);
  } else {
    frameMode = "timeout";
    frameId = setTimeout(runFrame, RECHECK_INTERVAL_MS);
  }
}

function runFrame(deadline) {
  frameId = null;
  frameMode = null;
  // Re-check interaction freshness — the user could have started
  // interacting between when we scheduled and when we ran. Bail and
  // let the recheck timer pick it up.
  if (isPaused()) {
    if (queue.length > 0) {
      scheduleNextFrame();
    }
    return;
  }
  const start = now();
  // Two modes:
  //
  //   - Initial burst (fresh load or armed hydration window): drain up to
  //     ~14 ms per frame until the window disarms / first interaction outside one.
  //
  //   - Steady state (after burst ends — user edits, paste #2, DnD):
  //     ONE job per frame. A single mount can take 20-30 ms and JS
  //     can't be preempted mid-job, so a pointermove arriving mid-
  //     job has to wait. Capping at one job bounds the worst-case
  //     pause-and-resume to one frame's mount, with a full settle
  //     window before the next.
  const burst = isInitialBurstActive();
  const pointerActive = lastInteractionAt !== 0
    && now() - lastInteractionAt < INTERACTION_QUIESCE_MS;
  // Pointer moving in-window: keep mounting at the smaller budget.
  let budget = 0;
  if (burst) {
    budget = pointerActive ? FRAME_BUDGET_MS : INITIAL_BURST_FRAME_BUDGET_MS;
  }
  let didRun = false;
  while (queue.length > 0) {
    const job = queue.shift();
    if (job.cancelled) {
      continue;
    }
    try {
      job.fn();
      didRun = true;
    } catch (err) {
      // One bad job shouldn't stall every other frame's hydration.
      // eslint-disable-next-line no-console
      console.error("[mount-scheduler] job failed", err);
    } finally {
      // Settle before decrementing: stale cancel handles must be no-ops.
      job.cancelled = true;
      bumpFrameJobCount(job.frameKey, -1);
    }
    // Steady state: yield after one job.
    if (!burst) {
      break;
    }
    // Burst: stay in the frame until we hit the budget or run out of
    // work. Re-check interaction freshness — a pointermove that lands
    // partway through the burst should still preempt; we yield on the
    // next iteration so the user gets responsiveness even mid-burst.
    if (isPaused()) {
      break;
    }
    if (now() - start >= budget) {
      break;
    }
  }
  void deadline;
  void didRun;

  if (queue.length > 0) {
    scheduleNextFrame();
  } else {
    maybeNotifyDrain();
  }
}

export function scheduleMount(fn, frameKey = null) {
  const job = { id: nextJobId++, fn, cancelled: false, frameKey };
  queue.push(job);
  hasBeenBusy = true;
  bumpFrameJobCount(frameKey, 1);
  if (drainTimer != null) {
    clearTimeout(drainTimer);
    drainTimer = null;
  }
  scheduleNextFrame();
  return () => {
    if (!job.cancelled) {
      job.cancelled = true;
      bumpFrameJobCount(job.frameKey, -1);
    }
  };
}

export function pendingMountJobs() {
  return queue.length;
}

// Exported drag-state probe — canvas + panel hover handlers query this
// to suppress store writes while the user is actively dragging. Cursor-
// hover updates during a pan are pure overhead (the user is navigating,
// not editing) and the visual-flicker of edit borders firing as the
// cursor crosses elements during a pan is the user-perceived "glitchy"
// pan feel.
export function isUserDragging() {
  return isDragging;
}

// Subscribe to GLOBAL "queue drained" events (paste flow). Breakpoint
// frames reveal via the per-frame onFrameDrain instead.
export function onDrain(handler) {
  drainSubscribers.add(handler);
  return () => drainSubscribers.delete(handler);
}

function maybeNotifyDrain() {
  if (!hasBeenBusy || queue.length > 0) {
    return;
  }
  if (drainTimer != null) {
    return;
  }
  drainTimer = setTimeout(() => {
    drainTimer = null;
    if (queue.length > 0) {
      // New work landed during the settle window — wait for the next
      // drain.
      return;
    }
    hasBeenBusy = false;
    for (const cb of drainSubscribers) {
      try {
        cb();
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("[mount-scheduler] drain subscriber threw", err);
      }
    }
  }, DRAIN_SETTLE_MS);
}

// Test/debug hook — lets specs reset interaction state without poking at
// internals. Not for production use.
export function _resetMountSchedulerForTests() {
  queue.length = 0;
  cancelScheduledFrame();
  clearRecheck();
  lastInteractionAt = 0;
  hydrationWindowEndsAt = 0;
  hasBeenBusy = false;
  frameJobCounts.clear();
  for (const timer of frameDrainTimers.values()) {
    clearTimeout(timer);
  }
  frameDrainTimers.clear();
  frameDrainSubscribers.clear();
}
